import { morph, MORPH_OPTIONS } from '@theme/morph';

const SECTION_ID_PREFIX = 'shopify-section-';

let didNavigateWithPills = false;
let popstateBound = false;

function bindVariantPillsPopState() {
  if (popstateBound) return;
  popstateBound = true;
  window.addEventListener('popstate', () => {
    if (!didNavigateWithPills && history.state?.variantPills !== true) return;
    window.location.reload();
  });
}

/**
 * Linked-product variant pills. Switching must stay clickable even when the
 * selected product is sold out — full-page navigation can leave the PDP stuck
 * in a loading state and block the other pills.
 */
class VariantPickerPills extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number} */
  #requestId = 0;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    bindVariantPillsPopState();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#abortController?.abort();
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const pill = event.target instanceof Element ? event.target.closest('a.variant-picker-pills__pill') : null;
    if (!(pill instanceof HTMLAnchorElement) || !this.contains(pill)) return;

    const nextUrl = pill.href;
    if (!nextUrl) return;

    if (pill.classList.contains('variant-picker-pills__pill--active') || this.#isCurrentUrl(nextUrl)) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.#loadProduct(nextUrl, pill);
  };

  /**
   * @param {string} url
   * @returns {boolean}
   */
  #isCurrentUrl(url) {
    try {
      const next = new URL(url, window.location.origin);
      const current = new URL(window.location.href);
      return next.pathname === current.pathname;
    } catch {
      return false;
    }
  }

  /**
   * @param {string} url
   * @param {HTMLAnchorElement | null} pill
   */
  async #loadProduct(url, pill) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const requestId = ++this.#requestId;

    this.dataset.loading = 'true';
    this.#markPendingPill(pill);

    try {
      const section = this.closest('.shopify-section');
      const sectionId = section?.id?.startsWith(SECTION_ID_PREFIX) ? section.id.slice(SECTION_ID_PREFIX.length) : '';
      const requestUrl = new URL(url, window.location.origin);
      if (sectionId) requestUrl.searchParams.set('section_id', sectionId);

      const response = await fetch(requestUrl.toString(), {
        signal,
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) throw new Error(`Variant product request failed: ${response.status}`);

      const responseText = await response.text();
      if (requestId !== this.#requestId) return;

      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const currentSection = this.closest('.shopify-section');
      const newSection =
        (sectionId && html.getElementById(`${SECTION_ID_PREFIX}${sectionId}`)) ||
        html.querySelector('.shopify-section') ||
        html.querySelector('main');

      if (!currentSection || !newSection) throw new Error('Variant product markup missing');

      morph(currentSection, newSection, MORPH_OPTIONS);

      const canonicalUrl = new URL(url, window.location.origin);
      canonicalUrl.searchParams.delete('section_id');
      history.pushState({ variantPills: true }, '', canonicalUrl.pathname + canonicalUrl.search + canonicalUrl.hash);
      didNavigateWithPills = true;

      const heading = document.querySelector('.product-details h1, .product-title, [data-testid="product-title"]');
      if (heading?.textContent) {
        const siteName = document.title.includes('–') ? document.title.split('–').pop()?.trim() : '';
        document.title = siteName ? `${heading.textContent.trim()} – ${siteName}` : heading.textContent.trim();
      }
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return;
      window.location.assign(url);
    } finally {
      if (requestId === this.#requestId) {
        this.dataset.loading = 'false';
        this.#markPendingPill(null);
      }
    }
  }

  /**
   * @param {HTMLAnchorElement | null} pill
   */
  #markPendingPill(pill) {
    this.querySelectorAll('.variant-picker-pills__pill--pending').forEach((element) => {
      element.classList.remove('variant-picker-pills__pill--pending');
    });
    pill?.classList.add('variant-picker-pills__pill--pending');
  }
}

if (!customElements.get('variant-picker-pills')) {
  customElements.define('variant-picker-pills', VariantPickerPills);
}
